import { type CSSProperties, type RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { BotIcon } from "../ui/BotIcon";
import { soundOn, synth, toggleSound } from "../game/sfx";
import { useSignal } from "../net/signal";
import { BoardView, type BoardHandle } from "./BoardView";
import type { Board } from "./board";
import { generateSolo } from "./shared";
import { HEARTS, type Solo, newSolo, soloTap, starsOf } from "./solo";
import { albumDone, albumKeyOf, loadBig, loadProgress, saveBig, saveStage, stageAt } from "./stages";
import { breakStreak, newStreak, outSound } from "./streak";
import { animalOf } from "./animals";
import { generateAnimal } from "./coop";
import { Thumb } from "./Thumb";
import { clock, clockTenths, fa } from "./fa";
import "./arrows.css";

/**
 * مرحله‌ی آفلاین (stages.ts، solo.ts): پازلِ تک‌نفره و تک‌رنگ روی نقشِ همان مرحله — سه قلب، ستاره از قلب‌های مانده
 * (کاربر، ۷ آبان: «آفلاین مرحله‌ای و تک‌رنگ»). مسابقه با دیگران فقط در اتاقِ دوستان است (Table.tsx).
 *
 * تخته‌ی تمام‌شده نقشِ کاشی‌کاری‌شده است و نقشِ دست‌ساز یا جانورِ تازه به آلبوم می‌رود (Album.tsx).
 *
 * تکه‌های صفحه — پیستِ مسابقه، جدولِ نتیجه، مدال، حلقه‌ی یخ، دکمه‌های بزرگ‌نمایی، جشن — صادر می‌شوند و میزِ آنلاین
 * با همان‌ها ساخته می‌شود. تخته روی بومِ سفیدِ تمام‌صفحه است و HUD ِ لاجوردی رویش شناور.
 */

/** یک خطِ پیست: هر بازیکن. */
export interface Lane {
  name: string;
  face: string;
  color: string;
  bot: boolean;
  cleared: number;
  total: number;
  done: boolean;
  /** میلی‌ثانیه از «برو» تا تمام‌کردن؛ null = هنوز. */
  finishedAt: number | null;
}

/**
 * ساعتِ مسابقه‌ی آفلاین: وقتی صفحه پنهان است نمی‌دود. مرورگر حلقه‌ی فریمِ برگه‌ی پنهان را نگه می‌دارد، پس
 * ربات‌ها هم می‌ایستادند ولی ساعتِ دیوار نه؛ برگشتن به صفحه مسابقه را با سقفِ چهار دقیقه و فلش‌های نیمه‌کاره
 * تمام می‌کرد (کاربر، ۷ آبان: پنلِ نتیجه‌ای که هیچ‌کس در آن تمام نکرده بود).
 */
function usePausingClock(): () => number {
  const st = useRef({ off: 0, since: -1 });
  useEffect(() => {
    const on = () => {
      const t = performance.now(), s = st.current;
      if (document.hidden) { if (s.since < 0) s.since = t; }
      else if (s.since >= 0) { s.off += t - s.since; s.since = -1; }
    };
    document.addEventListener("visibilitychange", on);
    return () => document.removeEventListener("visibilitychange", on);
  }, []);
  return useCallback(() => {
    const t = performance.now(), s = st.current;
    return t - s.off - (s.since >= 0 ? t - s.since : 0);
  }, []);
}

/** قلب‌های نقشه‌ی بزرگ: بیش از هزار فلش، چند برابرِ بزرگ‌ترین مرحله. */
const BIG_HEARTS = 8;
/** نقشه‌ی بزرگ: زیرِ این‌قدر پیکسل برای هر خانه، لمس نزدیک می‌کند و فلش نمی‌زند (BoardView.zoomTap). */
export const ZOOM_TAP = 16;

export function OfflineLevel({ stage, seed, big, onAgain, onNext, onMenu }: {
  stage: number;
  seed: number;
  /** نقشه‌ی بزرگِ جانور (animals.ts) به جای مرحله: پنج قلب، پیشرفتِ جدا، بی بازشدنِ مرحله. */
  big?: string;
  onAgain: () => void;
  onNext: () => void;
  onMenu: () => void;
}) {
  const st = stageAt(Math.max(0, stage));
  const beast = big ? animalOf(big) : undefined;
  const hearts0 = beast ? BIG_HEARTS : HEARTS;
  const title = beast ? { head: "نقشه‌ی بزرگ", name: beast.name } : { head: `مرحله‌ی ${fa(stage + 1)}`, name: st.name };
  const board = useMemo(() => (beast ? generateAnimal(seed, beast.id) : generateSolo(seed, st)), [seed, st, beast]);
  const [phase, setPhase] = useState<"intro" | "play" | "over">("intro");
  const game = useRef<Solo | null>(null);
  const [, setVer] = useState(0);
  const boardRef = useRef<BoardHandle>(null);
  const [hit, setHit] = useState(0);
  const timerEl = useRef<HTMLSpanElement>(null);
  const stageEl = useRef<HTMLDivElement>(null);
  const hud = useRef<HTMLDivElement>(null);
  const dock = useRef<HTMLDivElement>(null);
  const inset = useInsets(hud, dock);
  const sound = useSignal(soundOn);
  const gameNow = usePausingClock();
  /** پنلِ پایان: مرحله‌ی بعد تازه باز شد؟ رکوردِ تازه؟ */
  const [unlocked, setUnlocked] = useState(false);
  const [record, setRecord] = useState(false);
  /** نقشِ این مرحله تازه به آلبوم رفت؟ */
  const [fresh, setFresh] = useState(false);
  const streak = useRef(newStreak());

  // نامِ مرحله کوتاه روی تخته، بعد بازی. شمارش نیست: حریفی نیست که با او هم‌زمان شروع کنی.
  useEffect(() => {
    synth("chime");
    const t = setTimeout(() => { game.current = newSolo(board, gameNow(), hearts0); setPhase("play"); }, 1300);
    return () => clearTimeout(t);
  }, [board, gameNow]);

  // ساعت بی رندرِ React در هر فریم؛ با پنهان‌شدنِ صفحه می‌ایستد.
  useEffect(() => {
    if (phase !== "play") return;
    let raf = 0;
    const loop = () => {
      const g = game.current;
      if (g && timerEl.current) timerEl.current.textContent = clock(gameNow() - g.start);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [phase, gameNow]);

  const end = (g: Solo) => {
    const stars = starsOf(g);
    const key = beast ? `a:${beast.id}` : albumKeyOf(stage);
    setFresh(stars > 0 && !!key && !albumDone(loadProgress(), loadBig()).has(key));
    if (beast) {
      const best = loadBig()[beast.id]?.best;
      saveBig(beast.id, stars, g.finishedAt);
      setRecord(g.finishedAt !== null && best !== undefined && g.finishedAt < best);
    } else {
      const before = loadProgress();
      const best = before.best[String(stage)];
      saveStage(stage, stars, g.finishedAt);
      setUnlocked(stars > 0 && before.level <= stage);
      setRecord(g.finishedAt !== null && best !== undefined && g.finishedAt < best);
    }
    // پروازِ فلشِ آخر (یا برخوردِ آخر) تمام شود، بعد پنل. برد: اول نقشِ کاشی‌شده و موجِ رویش (BoardView.done).
    setTimeout(() => {
      synth(stars > 0 ? "fanfare" : "stamp");
      setPhase("over");
    }, stars > 0 ? 1900 : 550);
  };

  const onTap = (id: number) => {
    const g = game.current;
    if (!g || phase !== "play") return;
    const t = soloTap(g, id, gameNow());
    if (t.kind === "out") {
      boardRef.current?.fly(id, t.dist);
      outSound(streak.current);
      if (t.won) end(g);
    } else if (t.kind === "blocked") {
      boardRef.current?.bump(id, t.dist);
      synth("stamp");
      breakStreak(streak.current);
      shake(stageEl.current);
      setHit((h) => h + 1);
      try { navigator.vibrate?.(t.lost ? [60, 60, 120] : 70); } catch { /* بی لرزش هم کار می‌کند */ }
      if (t.lost) end(g);
    }
    setVer((v) => v + 1);
  };

  const g = game.current;
  const left = g?.left ?? board.arrows.length;
  const hearts = g?.hearts ?? hearts0;
  const pct = ((board.arrows.length - left) / Math.max(1, board.arrows.length)) * 100;

  return (
    <div className="ar-race solo">
      <div className="ar-stage" ref={stageEl} onAnimationEnd={(e) => { if (e.target === stageEl.current) stageEl.current.classList.remove("shake"); }}>
        <BoardView ref={boardRef} board={board} alive={g?.alive ?? board.arrows.map(() => true)} onTap={onTap} inset={inset} locked={phase !== "play"}
          zoomTap={beast ? ZOOM_TAP : undefined} done={g?.finishedAt != null} />
      </div>
      {hit > 0 && <div className="ar-hurt" key={hit} aria-hidden />}

      <div className="ar-hud" ref={hud}>
        <div className="ar-top">
          <button type="button" className="ar-icon" onClick={onMenu} aria-label="بیرون رفتن">
            <svg viewBox="0 0 24 24" aria-hidden><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
          <div className="ar-clock"><span ref={timerEl}>{clock(0)}</span></div>
          <button type="button" className="ar-icon" onClick={toggleSound} aria-label={sound ? "صدا خاموش" : "صدا روشن"}>
            <svg viewBox="0 0 24 24" aria-hidden>
              <path d="M4 9h4l5-4v14l-5-4H4z" className="fill" />
              {sound ? <path d="M16.5 8.5a5 5 0 0 1 0 7M19 6a8.5 8.5 0 0 1 0 12" /> : <path d="M16 9l6 6M22 9l-6 6" />}
            </svg>
          </button>
        </div>
        <div className="ar-solobar">
          <span className={"ar-hearts" + (hearts0 > 6 ? " many" : "")} aria-label={`${fa(hearts)} قلب`}>
            {Array.from({ length: hearts0 }, (_, k) => (
              <svg key={k} viewBox="0 0 24 24" className={k < hearts ? "on" : "off"} aria-hidden>
                <path d="M12 20.8 3.6 12.4a5 5 0 0 1 7.1-7.1l1.3 1.3 1.3-1.3a5 5 0 0 1 7.1 7.1z" />
              </svg>
            ))}
          </span>
          <span className="ar-solo-name"><small>{title.head}</small><b>{title.name}</b></span>
          <span className="ar-solo-left"><b>{fa(left)}</b><small>مانده</small></span>
          <i className="ar-solo-bar" style={{ "--p": `${pct}` } as CSSProperties}><i /></i>
        </div>
      </div>

      <Dock refEl={dock} board={boardRef} />

      {phase === "intro" && <Intro head={title.head} name={title.name} />}

      {phase === "over" && g && (
        <SoloResult game={g} stage={stage} title={title} big={beast?.id} unlocked={unlocked} record={record} fresh={fresh}
          onAgain={onAgain} onNext={onNext} onMenu={onMenu} />
      )}
    </div>
  );
}

/** چهارگوشِ دورِ فلش‌های چند صندلی (سهمِ من، یا سهمِ تیمم)، به واحدِ خانه. */
export function teamBox(b: Board, seats: number[]) {
  let x0 = b.w, y0 = b.h, x1 = 0, y1 = 0;
  for (const a of b.arrows) {
    if (!seats.includes(a.owner ?? -1)) continue;
    for (const c of a.cells) {
      const x = c % b.w, y = Math.floor(c / b.w);
      x0 = Math.min(x0, x); y0 = Math.min(y0, y); x1 = Math.max(x1, x + 1); y1 = Math.max(y1, y + 1);
    }
  }
  return { x: x0, y: y0, w: Math.max(1, x1 - x0), h: Math.max(1, y1 - y0) };
}

export function shake(el: HTMLElement | null): void {
  // لرزش از نو، بی سوارکردنِ دوباره‌ی تخته (که انیمیشنِ برخورد را می‌بُرید).
  if (el) { el.classList.remove("shake"); void el.offsetWidth; el.classList.add("shake"); }
}

/** تخته زیرِ HUD نمی‌نشیند: بلندیِ HUD ِ بالا و دکمه‌های پایین از خودِ صفحه خوانده می‌شود. */
export function useInsets(hud: RefObject<HTMLElement | null>, dock: RefObject<HTMLElement | null>) {
  const [inset, setInset] = useState({ top: 160, bottom: 90 });
  useLayoutEffect(() => {
    const read = () => {
      const box = hud.current?.parentElement?.getBoundingClientRect();
      const top = box?.top ?? 0, bottom = box?.bottom ?? window.innerHeight;
      setInset({
        top: (hud.current?.getBoundingClientRect().bottom ?? 150) - top + 8,
        bottom: dock.current ? bottom - dock.current.getBoundingClientRect().top + 8 : 90,
      });
    };
    read();
    window.addEventListener("resize", read);
    return () => window.removeEventListener("resize", read);
  }, [hud, dock]);
  return inset;
}

/** نامِ مرحله یا نقش بالای شمارش. */
export function Intro({ head, name, teams }: { head: string; name: string; teams?: boolean }) {
  return <div className="ar-intro"><span>{head}</span><b>{name}</b>{teams && <em>تیمی</em>}</div>;
}

export function FreezeRing({ k, ms }: { k: number; ms: number }) {
  return (
    <div className="ar-freeze" key={k} style={{ "--ms": `${ms}ms` } as CSSProperties}>
      <div className="ar-ring">
        <svg viewBox="0 0 100 100" aria-hidden><circle cx="50" cy="50" r="44" pathLength={1} /></svg>
        <b>یخ زدی!</b>
      </div>
    </div>
  );
}

export function Dock({ refEl, board }: { refEl: RefObject<HTMLDivElement | null>; board: RefObject<BoardHandle | null> }) {
  return (
    <div className="ar-dock" ref={refEl}>
      <button type="button" className="ar-zoom" onClick={() => board.current?.zoomBy(1 / 1.35)} aria-label="کوچک‌تر">
        <svg viewBox="0 0 24 24" aria-hidden><path d="M6 12h12" /></svg>
      </button>
      <button type="button" className="ar-zoom fit" onClick={() => board.current?.fit()} aria-label="فلش‌های من">
        <svg viewBox="0 0 24 24" aria-hidden><path d="M4 9V4h5M20 9V4h-5M4 15v5h5M20 15v5h-5" /></svg>
      </button>
      <button type="button" className="ar-zoom" onClick={() => board.current?.zoomBy(1.35)} aria-label="بزرگ‌تر">
        <svg viewBox="0 0 24 24" aria-hidden><path d="M6 12h12M12 6v12" /></svg>
      </button>
    </div>
  );
}

/** ترتیبِ زنده‌ی بازیکن‌ها: تمام‌کرده‌ها به ترتیبِ زمان، بعد بقیه به سهمی که برداشته‌اند. */
function standing(lanes: Lane[]): number[] {
  return lanes.map((_, i) => i).sort((x, y) => {
    const a = lanes[x]!, b = lanes[y]!;
    if (a.finishedAt !== null || b.finishedAt !== null) {
      if (a.finishedAt === null) return 1;
      if (b.finishedAt === null) return -1;
      return a.finishedAt - b.finishedAt;
    }
    return b.cleared / Math.max(1, b.total) - a.cleared / Math.max(1, a.total) || x - y;
  });
}

/** دو تیم به ترتیب: تیمی که هر دو نفرش تمام کرده‌اند (زودتر)، بعد به شمارِ فلشِ برداشته‌ی هر دو. */
function teamStanding(lanes: Lane[]): number[] {
  const of = (t: number) => {
    const ls = [lanes[t], lanes[t + 2]].filter((l): l is Lane => !!l);
    const fin = ls.every((l) => l.finishedAt !== null) ? Math.max(...ls.map((l) => l.finishedAt!)) : null;
    return { fin, cleared: ls.reduce((n, l) => n + l.cleared, 0) };
  };
  const [a, b] = [of(0), of(1)];
  const first = a.fin !== null || b.fin !== null
    ? (b.fin === null || (a.fin !== null && a.fin <= b.fin) ? 0 : 1)
    : (b.cleared > a.cleared ? 1 : 0);
  return [first, 1 - first];
}

/**
 * پیستِ مسابقه: هر بازیکن یک جاده به رنگِ فلش‌هایش. سرِ هر جاده جایگاهِ زنده (اولی طلایی)، نام و «N مانده» (تمام‌کرده:
 * زمانش)، و تهِ همه‌ی جاده‌ها نوارِ شطرنجیِ پایان که با هم یک خطِ پایان می‌سازند. چهره روی جاده تا آن‌جا جلو
 * می‌رود که فلش برداشته؛ جلوداری تاج دارد و تمام‌کرده تیک. تیمی: دو جاده‌ی هر تیم زیرِ سرِ تیم، با جایگاه و
 * فلش‌های مانده‌ی تیم.
 */
export function Track({ lanes, pulses, teams, me = 0 }: { lanes: Lane[]; pulses: number[]; teams: boolean; me?: number }) {
  const order = standing(lanes);
  const lead = lanes.some((l) => l.cleared > 0) ? order[0]! : -1;
  const rail = (i: number, rank: number) => {
    const l = lanes[i]!;
    const pct = (l.cleared / Math.max(1, l.total)) * 100;
    return (
      <div key={i} className={"ar-rail" + (i === me ? " me" : "") + (l.done ? " done" : "") + (i === lead ? " lead" : "")}
        style={{ "--rc": l.color, "--p": `${pct}` } as CSSProperties}>
        {rank > 0 && <span className="ar-rank" key={rank}>{fa(rank)}</span>}
        <span className="ar-who">
          <b>{l.name}</b>
          <small>{l.finishedAt !== null ? clock(l.finishedAt) : `${fa(l.total - l.cleared)} مانده`}</small>
        </span>
        <span className="ar-road">
          <i className="ar-fill" />
          <span className="ar-runner">
            <img src={l.face} alt="" />
            {l.bot && <span className="ar-botbadge"><BotIcon size={9} /></span>}
            {i === lead && !l.done && (
              <svg className="ar-crownlead" viewBox="0 0 24 24" aria-hidden><path d="M3 8l4.5 4L12 5l4.5 7L21 8l-2 11H5z" /></svg>
            )}
            {l.done && <svg className="ar-tick" viewBox="0 0 24 24" aria-hidden><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>}
            {pulses[i]! > 0 && <em key={pulses[i]} />}
          </span>
        </span>
      </div>
    );
  };
  if (!teams) {
    return <div className={`ar-track n${lanes.length}`}>{lanes.map((_, i) => rail(i, order.indexOf(i) + 1))}</div>;
  }
  // تماشاچی (me −۱) تیمِ خودی ندارد: «تیم ۱» و «تیم ۲»
  const mine = Math.max(0, me) % 2, tOrder = teamStanding(lanes);
  const teamName = (t: number) => (me < 0 ? `تیم ${fa(t + 1)}` : t === mine ? "تیم شما" : "تیم حریف");
  return (
    <div className="ar-track teams">
      {[mine, 1 - mine].map((t) => {
        const seats = [t, t + 2].filter((i) => i < lanes.length);
        const left = seats.reduce((n, i) => n + lanes[i]!.total - lanes[i]!.cleared, 0);
        const first = tOrder[0] === t && seats.some((i) => lanes[i]!.cleared > 0);
        return (
          <div key={t} className={"ar-group" + (t === mine ? " mine" : "") + (first ? " lead" : "")}>
            <div className="ar-group-h">
              <span className="ar-rank" key={tOrder.indexOf(t)}>{fa(tOrder.indexOf(t) + 1)}</span>
              <b>{teamName(t)}</b>
              <small>{left ? `${fa(left)} مانده` : "تمام!"}</small>
            </div>
            {seats.map((i) => rail(i, 0))}
          </div>
        );
      })}
    </div>
  );
}

/**
 * جدولِ نتیجه: هر بازیکن با نشانِ جایگاه (طلا، نقره، مفرغ)، چهره، نام، نوارِ نازکِ پیشرفت به رنگِ فلش‌هایش، و زمانِ
 * تمام‌کردن یا «۹/۱۹». یخ‌زدن فقط وقتی بوده، با نشانِ برف. تیمی: دو کارتِ تیم، کارتِ برنده طلایی، با جایگاه و
 * زمانِ تیم (دیرترین هم‌تیمی) یا فلش‌های برداشته‌ی تیم.
 */
export function Standings({ lanes, teams, me = 0, mistakes }: { lanes: Lane[]; teams: boolean; me?: number; mistakes: (i: number) => number }) {
  const value = (l: Lane) => (l.finishedAt !== null ? clockTenths(l.finishedAt) : `${fa(l.cleared)}/${fa(l.total)}`);
  const row = (i: number, place: number) => {
    const l = lanes[i]!;
    const m = mistakes(i);
    return (
      <li key={i} className={"ar-st" + (i === me ? " me" : "")} style={{ "--rc": l.color, "--p": `${(l.cleared / Math.max(1, l.total)) * 100}` } as CSSProperties}>
        {place > 0 && <span className={`ar-pl p${place}`}>{fa(place)}</span>}
        <img src={l.face} alt="" />
        <span className="ar-st-who">
          <b>{l.name}{l.bot && <BotIcon size={11} />}</b>
          <i className="ar-st-bar"><i /></i>
        </span>
        <span className="ar-st-val">
          <b>{value(l)}</b>
          {m > 0 && (
            <small title="لمس اشتباه">
              <svg viewBox="0 0 24 24" aria-hidden><path d="M12 2v20M3.3 7l17.4 10M3.3 17L20.7 7M9 4l3 3 3-3M9 20l3-3 3 3" /></svg>
              {fa(m)}
            </small>
          )}
        </span>
      </li>
    );
  };
  if (!teams) return <ol className="ar-standings">{standing(lanes).map((i, k) => row(i, k + 1))}</ol>;
  const order = standing(lanes);
  return (
    <div className="ar-teams">
      {teamStanding(lanes).map((t, k) => {
        const seats = order.filter((i) => i % 2 === t);
        const ls = seats.map((i) => lanes[i]!);
        const fin = ls.every((l) => l.finishedAt !== null) ? Math.max(...ls.map((l) => l.finishedAt!)) : null;
        const sum = ls.reduce((n, l) => n + l.cleared, 0), tot = ls.reduce((n, l) => n + l.total, 0);
        return (
          <section key={t} className={"ar-teamcard" + (k === 0 ? " win" : "") + (me >= 0 && t === me % 2 ? " mine" : "")}>
            <header>
              <span className={`ar-pl p${k + 1}`}>{fa(k + 1)}</span>
              <b>{me < 0 ? `تیم ${fa(t + 1)}` : t === me % 2 ? "تیم شما" : "تیم حریف"}</b>
              <span className="ar-st-val"><b>{fin !== null ? clockTenths(fin) : `${fa(sum)}/${fa(tot)}`}</b></span>
            </header>
            <ol className="ar-standings">{seats.map((i) => row(i, 0))}</ol>
          </section>
        );
      })}
    </div>
  );
}

/** مدالِ بالای پنلِ نتیجه: جایگاه با طلا، نقره یا مفرغ؛ اولی تاج دارد. */
export function Medal({ place }: { place: number }) {
  return (
    <div className={`ar-medal p${place}`} aria-hidden>
      <svg viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="46" className="rim" />
        <circle cx="50" cy="50" r="38" className="disc" />
        {Array.from({ length: 16 }, (_, k) => {
          const a = (Math.PI / 8) * k;
          return <line key={k} x1={50 + 40 * Math.cos(a)} y1={50 + 40 * Math.sin(a)} x2={50 + 44 * Math.cos(a)} y2={50 + 44 * Math.sin(a)} className="tick" />;
        })}
      </svg>
      {place === 1
        ? <svg className="crown" viewBox="0 0 24 24"><path d="M3 8l4.5 4L12 5l4.5 7L21 8l-2 11H5z" /></svg>
        : <b>{fa(place)}</b>}
    </div>
  );
}

/** پایانِ مرحله‌ی آفلاین: ستاره‌ها (یا دلِ شکسته)، زمان و رکورد، نقشِ تازه‌ی آلبوم، و مرحله‌ی بعد یا دوباره. */
function SoloResult({ game, stage, title: name, big, unlocked, record, fresh, onAgain, onNext, onMenu }: {
  game: Solo; stage: number; title: { head: string; name: string }; big?: string; unlocked: boolean; record: boolean; fresh: boolean;
  onAgain: () => void; onNext: () => void; onMenu: () => void;
}) {
  const stars = starsOf(game);
  const won = stars > 0;
  const title = !won ? "قلب‌ها تمام شد" : stars === 3 ? "بی‌نقص!" : "تمام شد!";
  return (
    <div className="ar-veil">
      {won && <Confetti />}
      <div className={"ar-result solo" + (won ? " win" : "")}>
        {won ? (
          <div className="ar-stars" aria-label={`${fa(stars)} ستاره`}>
            {[1, 2, 3].map((k) => (
              <svg key={k} viewBox="0 0 24 24" className={k <= stars ? "on" : "off"} style={{ animationDelay: `${0.15 + k * 0.18}s` }} aria-hidden>
                <path d="M12 2.5l2.9 5.9 6.6.9-4.8 4.6 1.2 6.5-5.9-3.1-5.9 3.1 1.2-6.5L2.5 9.3l6.6-.9z" />
              </svg>
            ))}
          </div>
        ) : (
          <svg className="ar-broken" viewBox="0 0 24 24" aria-hidden>
            <path d="M12 20.8 3.6 12.4a5 5 0 0 1 7.1-7.1l1.3 1.3 1.3-1.3a5 5 0 0 1 7.1 7.1z" />
            <path d="M12 6.6l-1.6 3.6 2.6 1.8-2.2 4" className="crack" />
          </svg>
        )}
        <div className="ar-title">
          <b>{title}</b>
          <span>
            {name.head} · {name.name}
            {won && game.finishedAt !== null && <> · <em>{clockTenths(game.finishedAt)}</em></>}
            {!won && <> · <em>{fa(game.left)} فلش مانده بود</em></>}
            {record && <i className="ar-record">رکورد تازه</i>}
          </span>
        </div>
        {fresh && (
          <div className="ar-unlock ar-fresh">
            {big ? <Thumb animal={big} tiled /> : <Thumb stage={stage} tiled />}
            <span>به آلبوم اضافه شد</span>
          </div>
        )}
        {unlocked && (
          <div className="ar-unlock">
            <Thumb stage={stage + 1} />
            <span>مرحله‌ی {fa(stage + 2)} باز شد</span>
          </div>
        )}
        <div className="ar-actions">
          <button type="button" className="btn ghost" onClick={onMenu}>مرحله‌ها</button>
          {won
            ? <button type="button" className="btn" onClick={onNext}>{big !== undefined ? "نقشه‌ی بعد" : "مرحله‌ی بعد"}</button>
            : <button type="button" className="btn" onClick={onAgain}>دوباره</button>}
        </div>
      </div>
    </div>
  );
}

const CONFETTI = ["#e9cd7e", "#c9a24a", "#f6e7d4", "#447c8d", "#b3443a", "#377d4f"];

export function Confetti() {
  const bits = useMemo(() => Array.from({ length: 42 }, (_, i) => ({
    left: Math.random() * 100,
    delay: Math.random() * 0.5,
    drift: (Math.random() - 0.5) * 160,
    spin: (Math.random() - 0.5) * 900,
    color: CONFETTI[i % CONFETTI.length]!,
  })), []);
  return (
    <div className="ar-confetti" aria-hidden>
      {bits.map((b, i) => (
        <i key={i} style={{ left: `${b.left}%`, background: b.color, animationDelay: `${b.delay}s`,
          "--drift": `${b.drift}px`, "--spin": `${b.spin}deg` } as CSSProperties} />
      ))}
    </div>
  );
}
